import React, { useState, useEffect } from 'react';
import './StockManagement.css';
import { API_URL } from '../config/api';

const StockManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formMode, setFormMode] = useState('single'); // 'single' ou 'variants'
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    size: '',
    characteristics: '',
    stock_quantity: 0
  });
  const [variants, setVariants] = useState([{ size: '', stock_quantity: 0 }]);
  const [alert, setAlert] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      // Ajouter un timestamp pour éviter le cache
      const response = await fetch(`${API_URL}/api/items?t=${Date.now()}`);
      const data = await response.json();
      setItems(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des éléments:', error);
      setLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // Protection anti-double-clic
    if (isSubmitting) return;
    setIsSubmitting(true);

    // Si on est en mode édition, utiliser l'ancien système
    if (editingItem) {
      try {
        const response = await fetch(`${API_URL}/api/items/${editingItem.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          showAlert('Élément mis à jour avec succès', 'success');
          resetForm();
          fetchItems();
        } else {
          const error = await response.json();
          showAlert(error.error || 'Erreur lors de la sauvegarde', 'error');
        }
      } catch (error) {
        console.error('Erreur:', error);
        showAlert('Erreur lors de la sauvegarde', 'error');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Mode création : simple ou variantes
    if (formMode === 'variants') {
      // Créer plusieurs articles avec variantes
      const validVariants = variants.filter(v => v.size && v.size.trim() !== '');
      if (validVariants.length === 0) {
        showAlert('Veuillez ajouter au moins une variante avec une taille', 'error');
        return;
      }

      try {
        const promises = validVariants.map(variant => 
          fetch(`${API_URL}/api/items`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name: formData.name,
              description: formData.description,
              size: variant.size.trim(),
              characteristics: formData.characteristics,
              stock_quantity: variant.stock_quantity || 0
            }),
          })
        );

        const results = await Promise.all(promises);
        const errors = results.filter(r => !r.ok);
        
        if (errors.length === 0) {
          showAlert(`${validVariants.length} variante(s) créée(s) avec succès`, 'success');
          resetForm();
          fetchItems();
        } else {
          showAlert('Certaines variantes n\'ont pas pu être créées', 'error');
          fetchItems();
        }
      } catch (error) {
        console.error('Erreur:', error);
        showAlert('Erreur lors de la création des variantes', 'error');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Mode création simple (ancien système)
      try {
        const response = await fetch(`${API_URL}/api/items`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          showAlert('Élément créé avec succès', 'success');
          resetForm();
          fetchItems();
        } else {
          const error = await response.json();
          showAlert(error.error || 'Erreur lors de la sauvegarde', 'error');
        }
      } catch (error) {
        console.error('Erreur:', error);
        showAlert('Erreur lors de la sauvegarde', 'error');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      size: item.size || '',
      characteristics: item.characteristics || '',
      stock_quantity: item.stock_quantity
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) {
      return;
    }

    // Retirer immediatement de la liste locale pour eviter double-clic
    setItems(prev => prev.filter(item => item.id !== id));

    try {
      const response = await fetch(`${API_URL}/api/items/${id}`, {
        method: 'DELETE'
      });

      if (response.ok || response.status === 404) {
        // 404 = deja supprime (doublon), on considere comme succes
        showAlert('Élément supprimé avec succès', 'success');
      } else {
        showAlert('Erreur lors de la suppression', 'error');
      }
      // Toujours rafraichir la liste depuis le serveur
      fetchItems();
    } catch (error) {
      console.error('Erreur:', error);
      showAlert('Erreur lors de la suppression', 'error');
      fetchItems();
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      size: '',
      characteristics: '',
      stock_quantity: 0
    });
    setVariants([{ size: '', stock_quantity: 0 }]);
    setFormMode('single');
    setEditingItem(null);
    setShowForm(false);
  };

  const addVariant = () => {
    setVariants([...variants, { size: '', stock_quantity: 0 }]);
  };

  const removeVariant = (index) => {
    if (variants.length > 1) {
      setVariants(variants.filter((_, i) => i !== index));
    }
  };

  const updateVariant = (index, field, value) => {
    const updated = [...variants];
    updated[index][field] = field === 'stock_quantity' ? (parseInt(value) || 0) : value;
    setVariants(updated);
  };

  // Grouper les articles par nom (pour afficher les variantes ensemble)
  const groupedItems = items.reduce((acc, item) => {
    const key = item.name.toLowerCase().trim();
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {});

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div className="stock-management">
      <div className="page-header">
        <h1>Gestion du stock</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Annuler' : 'Ajouter un élément'}
        </button>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type === 'error' ? 'error' : 'success'}`}>
          {alert.message}
        </div>
      )}

      {showForm && (
        <div className="card form-card">
          <h2>{editingItem ? 'Modifier l\'élément' : 'Nouvel élément'}</h2>
          
          {!editingItem && (
            <div className="form-mode-selector">
              <label>
                <input
                  type="radio"
                  value="single"
                  checked={formMode === 'single'}
                  onChange={(e) => setFormMode(e.target.value)}
                />
                Article simple
              </label>
              <label>
                <input
                  type="radio"
                  value="variants"
                  checked={formMode === 'variants'}
                  onChange={(e) => setFormMode(e.target.value)}
                />
                Article avec variantes (ex: plusieurs tailles)
              </label>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Nom *</label>
              <input
                type="text"
                className="form-control"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Chaussures de sécurité"
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                className="form-control"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description générale de l'article"
              />
            </div>

            {formMode === 'variants' && !editingItem ? (
              <div className="variants-section">
                <div className="variants-header">
                  <h3>Variantes (tailles, couleurs, etc.)</h3>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addVariant}>
                    + Ajouter une variante
                  </button>
                </div>
                {variants.map((variant, index) => (
                  <div key={index} className="variant-row">
                    <div className="form-group">
                      <label>Taille / Variante *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={variant.size}
                        onChange={(e) => updateVariant(index, 'size', e.target.value)}
                        placeholder="Ex: 40, 41, 42, Rouge, Bleu..."
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Quantité en stock</label>
                      <input
                        type="number"
                        className="form-control"
                        value={variant.stock_quantity}
                        onChange={(e) => updateVariant(index, 'stock_quantity', e.target.value)}
                        min="0"
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => removeVariant(index)}
                      disabled={variants.length === 1}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <div className="form-group">
                  <label>Caractéristiques communes</label>
                  <textarea
                    className="form-control"
                    value={formData.characteristics}
                    onChange={(e) => setFormData({ ...formData, characteristics: e.target.value })}
                    placeholder="Ex: Matériau, norme, etc. (appliqué à toutes les variantes)"
                  />
                </div>
              </div>
            ) : (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Taille</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.size}
                      onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                      placeholder="Ex: 40, M, L..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Quantité en stock</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.stock_quantity}
                      onChange={(e) => setFormData({ ...formData, stock_quantity: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Caractéristiques</label>
                  <textarea
                    className="form-control"
                    value={formData.characteristics}
                    onChange={(e) => setFormData({ ...formData, characteristics: e.target.value })}
                    placeholder="Ex: Matériau, couleur, etc."
                  />
                </div>
              </>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'En cours...' : editingItem ? 'Mettre à jour' : formMode === 'variants' ? 'Créer les variantes' : 'Créer'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <h2>Liste des éléments</h2>
        {items.length === 0 ? (
          <p>Aucun élément en stock.</p>
        ) : (
          <div className="items-list">
            {Object.entries(groupedItems).map(([key, groupItems]) => (
              <div key={key} className="item-group">
                <div className="item-group-header">
                  <h3>{groupItems[0].name}</h3>
                  {groupItems[0].description && (
                    <p className="item-group-description">{groupItems[0].description}</p>
                  )}
                  {groupItems[0].characteristics && (
                    <p className="item-group-characteristics">{groupItems[0].characteristics}</p>
                  )}
                </div>
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Taille / Variante</th>
                        <th>Stock</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupItems.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <strong>{item.size || 'Standard'}</strong>
                          </td>
                          <td>
                            <span className={item.stock_quantity > 0 ? 'stock-available' : 'stock-empty'}>
                              {item.stock_quantity}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleEdit(item)}
                            >
                              Modifier
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDelete(item.id)}
                            >
                              Supprimer
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StockManagement;

